import styled, { css } from 'styled-components';
import { Button, Cell, HeaderCell, PaginationFooter, Row, Table, TableBody, TableHead } from 'components';
import { lineHeight, theme, toRem } from 'design';

// Select Payees Table
// Mobile => flex
// 768 => table
export const StyledTable = styled(Table)`
  table tr:last-child th:first-child {
    border-top-left-radius: ${toRem(5.5)};
  }

  table tr:last-child th:last-child {
    border-top-right-radius: ${toRem(5.5)};
  }

  table tr:last-child td:first-child {
    border-bottom-left-radius: ${toRem(5.5)};
  }

  table tr:last-child td:last-child {
    border-bottom-right-radius: ${toRem(5.5)};
  }

  && {
    display: flex;
    flex-flow: column nowrap;
    justify-content: flex-start;
    align-items: flex-start;
    width: 100%;

    @media (min-width: ${theme.breakpoint.med}px) {
      display: table;
    }

    @media (max-width: ${theme.breakpoint.med}px) {
      th {
        border: none;
      }
    }

    ${TableHead} {
      display: flex;
      flex-flow: row nowrap;
      justify-content: flex-start;
      align-items: center;

      tr {
        display: flex;
        align-items: center;
      }

      @media (min-width: ${theme.breakpoint.med}px) {
        display: table-header-group;

        tr {
          display: table-row;
        }
      }
    }

    ${TableBody} {
      display: flex;
      flex-flow: column nowrap;
      justify-content: flex-start;
      align-items: flex-start;

      @media (min-width: ${theme.breakpoint.med}px) {
        display: table-row-group;
      }
    }
  }
`;

export const StyledRow = styled(Row)<{ $isDisabled?: boolean }>`
  && {
    display: flex;
    flex-flow: row nowrap;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0;
    padding: 0 ${toRem(theme.space.xl)};
    border-radius: 0;

    @media (max-width: ${theme.breakpoint.med}px) {
      td {
        border: none;
      }
    }

    @media (min-width: ${theme.breakpoint.med}px) {
      display: table-row;
      padding: 0;
    }
  }

  ${({ $isDisabled }) =>
    $isDisabled &&
    css`
      background-color: ${theme.color.white};

      p {
        color: ${theme.color.neutral[400]};
      }

      &:hover {
        background-color: ${theme.color.white};
      }
    `}
`;

const StyledCell = styled(Cell)`
  &::before {
    display: none;
  }
`;

// Columns are hidden and displayed as room becomes available
// The sidebar width has been compensated for here.
const Breakpoints = css`
  display: none;

  @media (min-width: ${theme.breakpoint.med}px) {
    display: table-cell;
  }

  @media (min-width: ${theme.breakpoint.large}px) {
    display: none;
  }

  @media (min-width: ${theme.breakpoint.xlarge}px) {
    display: table-cell;
  }
`;

export const JobTitleHeaderCell = styled(HeaderCell)`
  ${Breakpoints}
`;

export const JobTitleCell = styled(StyledCell)`
  ${Breakpoints}

  p {
    word-break: break-word;
    white-space: normal;
  }
`;

export const PayeeCell = styled(StyledCell)`
  display: flex;
  width: calc(100% - ${toRem(90)});
  height: unset;
  min-height: ${toRem(theme.table.cell.height)};
  padding: ${toRem(theme.space.md)} 0;

  @media (min-width: ${theme.breakpoint.med}px) {
    display: table-cell;
    padding: 0 ${toRem(theme.space.xl)};
  }
`;

// Action is absolutely positioned on mobile to allow for
// maximum length of job titles without interference from overflow or flex issues
export const CellAction = styled(StyledCell)`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  text-align: right;

  @media (min-width: ${theme.breakpoint.med}px) {
    position: relative;
    top: auto;
    right: auto;
    bottom: auto;
    display: table-cell;
  }
`;

export interface IAddButtonProps {
  isAdded?: boolean;
}

export const TableButton = styled(Button)<IAddButtonProps>`
  display: relative;
  width: ${toRem(70)};
  margin-left: auto;

  svg {
    display: none;
  }

  ${({ isAdded }) =>
    isAdded &&
    css`
      background-color: transparent;
      border: none;
      color: ${theme.color.secondary[400]};
      font-size: ${toRem(theme.font.size.xs)};
      line-height: ${lineHeight(theme.font.size.xs)};

      &:hover {
        background-color: transparent;
        border: none;
        color: ${theme.color.secondary[400]};
      }

      svg {
        display: inline-block;
        fill: ${theme.color.secondary[400]} !important;
      }
    `}
`;

export const PayeesPaginationFooter = styled(PaginationFooter)`
  align-items: center;
  padding: ${toRem(theme.space.xl)} 0 0;
`;
